import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, RefreshCwIcon } from 'lucide-react';
import clsx from 'clsx';
import { endOfMonth, format, startOfMonth, startOfYear, subMonths } from 'date-fns';
import { Button, Text } from '@/components/ui';
import { Select, SelectOption } from '@/components/ui/Select';
import { Spinner } from '@/components/Spinner';
import { groupsApi } from '@/lib/api/groups';
import { useResourceAllocation } from '@/hooks/useResourceAllocation';
import { ResourceAllocation } from '@/lib/api/reports';
import { Card, fmtDate, fmtHours, fmtPercent, Kpi } from './ContractConsumptionPage';

const day = (d: Date) => format(d, 'yyyy-MM-dd');

const PERIODS: Record<string, { label: string; range: () => [Date, Date] }> = {
  current: { label: 'Mês atual', range: () => [startOfMonth(new Date()), new Date()] },
  previous: {
    label: 'Mês anterior',
    range: () => [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))],
  },
  last3: {
    label: 'Últimos 3 meses',
    range: () => [startOfMonth(subMonths(new Date(), 2)), new Date()],
  },
  year: { label: 'Ano atual', range: () => [startOfYear(new Date()), new Date()] },
};
const PERIOD_OPTIONS: SelectOption[] = Object.entries(PERIODS).map(([value, p]) => ({
  value,
  content: p.label,
}));

// Occupancy bands: under-allocated, healthy, near the limit, overloaded
function band(p: number | null) {
  if (p === null) return { label: '-', bar: 'bg-neutral-strong', text: 'text-neutral-muted' };
  if (p > 100) return { label: 'Sobrecarga', bar: 'bg-error-default', text: 'text-error-default' };
  if (p >= 85) return { label: 'Alta', bar: 'bg-amber-500', text: 'text-amber-800' };
  if (p >= 50) return { label: 'Adequada', bar: 'bg-brand-default', text: 'text-neutral-default' };
  return { label: 'Baixa', bar: 'bg-neutral-strong', text: 'text-neutral-muted' };
}

export function ResourceAllocationPage() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('current');
  const [groupId, setGroupId] = useState('all');
  const [teams, setTeams] = useState<SelectOption[]>([]);

  const [startDate, endDate] = useMemo(() => PERIODS[period].range().map(day), [period]);
  const { data, isLoading, reload } = useResourceAllocation({
    startDate,
    endDate,
    groupId: groupId === 'all' ? undefined : groupId,
  });

  useEffect(() => {
    groupsApi
      .getAllForSelect()
      .then((groups) => setTeams(groups.map((g) => ({ value: g.id, content: g.name }))))
      .catch(() => setTeams([]));
  }, []);

  const teamOptions: SelectOption[] = [
    { value: 'all', content: 'Todas as equipes' },
    ...teams,
    { value: 'none', content: 'Sem equipe' },
  ];

  return (
    <div className="gap-xl flex flex-1 flex-col">
      <header className="gap-sm flex w-full flex-wrap items-center justify-between">
        <div className="gap-2xs flex items-center">
          <Link to="/reports" className="text-neutral-muted hover:text-neutral-default">
            <ArrowLeftIcon size={20} />
          </Link>
          <div>
            <Text as="h1" variant="heading">
              {t('reports.resourceAllocation.title')}
            </Text>
            {data && (
              <Text variant="label" size="xs" className="text-neutral-muted">
                {fmtDate(data.period.start)} a {fmtDate(data.period.end)} · {data.period.workdays}{' '}
                dia(s) útil(eis) considerados
              </Text>
            )}
          </div>
        </div>
        <div className="gap-xs flex flex-wrap items-center">
          <Select
            options={PERIOD_OPTIONS}
            width={170}
            value={period}
            onValueChange={(v) => setPeriod(v || 'current')}
          />
          <Select
            options={teamOptions}
            width={190}
            value={groupId}
            onValueChange={(v) => setGroupId(v || 'all')}
          />
          <Button variant="secondary" size="sm" onClick={reload} disabled={isLoading}>
            <RefreshCwIcon size={18} className={clsx(isLoading && 'animate-spin')} />
            Atualizar
          </Button>
        </div>
      </header>

      {!data && isLoading && (
        <div className="py-2xl flex justify-center">
          <Spinner color="black" />
        </div>
      )}

      {data && (
        <div className={clsx('gap-md flex flex-col', isLoading && 'opacity-60')}>
          <Kpis data={data} />

          <Card>
            <Text size="md" strong className="mb-xs block">
              Ocupação por analista
            </Text>
            <AnalystTable analysts={data.analysts} />
          </Card>

          <Card>
            <Text size="md" strong className="mb-xs block">
              Alocação por cliente
            </Text>
            <ClientTable clients={data.clients} />
          </Card>

          <Text variant="label" size="xs" className="text-neutral-muted">
            Horas efetivas = tempo apontado nos tickets (sem arredondamento nem débito em dobro).
            Capacidade = {data.weeklyHours}h semanais ({fmtHours(data.hoursPerWorkday)} por dia
            útil, segunda a sexta), contando só os dias já decorridos do período; feriados não são
            descontados. Ocupação acima de 100% indica sobrecarga. FTE = horas do cliente ÷
            capacidade de um analista no período. Entram os analistas com apontamento no período e,
            com filtro de equipe, também os membros sem apontamento.
          </Text>
        </div>
      )}
    </div>
  );
}

function Kpis({ data }: { data: ResourceAllocation }) {
  const s = data.summary;
  const b = band(s.occupancyPercent);
  return (
    <div className="gap-md grid grid-cols-2 lg:grid-cols-5">
      <Kpi label="Ocupação geral" value={fmtPercent(s.occupancyPercent)} hint={b.label} />
      <Kpi
        label="Horas efetivas"
        value={fmtHours(s.workedHours)}
        hint={`de ${fmtHours(s.capacityHours)} de capacidade`}
      />
      <Kpi
        label="Analistas"
        value={String(s.analystCount)}
        hint={`${fmtHours(data.analysts[0]?.capacityHours ?? 0)} de capacidade cada`}
      />
      <Kpi label="Clientes atendidos" value={String(s.clientCount)} />
      <Kpi label="Tickets trabalhados" value={String(s.ticketCount)} />
    </div>
  );
}

const cell = 'border-neutral-subtle px-xs py-2xs border-b align-middle';

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="bg-neutral-soft-surface border-neutral-subtle px-xs py-2xs border-b text-left">
      <Text variant="label" size="xs" strong className="text-neutral-strong uppercase">
        {children}
      </Text>
    </th>
  );
}

function OccupancyBar({ percent }: { percent: number | null }) {
  const b = band(percent);
  return (
    <div className="min-w-[160px]">
      <Text size="xs" className={b.text}>
        {fmtPercent(percent)} · {b.label}
      </Text>
      <div className="bg-neutral-subtle mt-4xs h-1.5 overflow-hidden rounded-full">
        <div
          className={clsx('h-full rounded-full', b.bar)}
          style={{ width: `${Math.min(100, percent ?? 0)}%` }}
        />
      </div>
    </div>
  );
}

const topList = (items: Array<{ name: string; hours: number }>, n = 3) =>
  items.length === 0
    ? '-'
    : items
        .slice(0, n)
        .map((i) => `${i.name} ${fmtHours(i.hours)}`)
        .join(' · ') + (items.length > n ? ` · +${items.length - n}` : '');

function AnalystTable({ analysts }: { analysts: ResourceAllocation['analysts'] }) {
  if (analysts.length === 0) {
    return <Text className="text-neutral-muted">Nenhum apontamento no período.</Text>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <Th>Analista</Th>
            <Th>Horas efetivas</Th>
            <Th>Ocupação</Th>
            <Th>Tickets</Th>
            <Th>Principais clientes</Th>
          </tr>
        </thead>
        <tbody>
          {analysts.map((a) => (
            <tr key={a.id} className="hover:bg-neutral-soft-surface">
              <td className={cell}>
                <Text strong>{a.name}</Text>
                <Text variant="label" size="xs" className="text-neutral-muted">
                  {a.groupName ?? 'Sem equipe'}
                </Text>
              </td>
              <td className={clsx(cell, 'whitespace-nowrap')}>
                <Text>{fmtHours(a.workedHours)}</Text>
                <Text variant="label" size="xs" className="text-neutral-muted">
                  de {fmtHours(a.capacityHours)}
                </Text>
              </td>
              <td className={cell}>
                <OccupancyBar percent={a.occupancyPercent} />
              </td>
              <td className={cell}>
                <Text>{a.ticketCount}</Text>
              </td>
              <td className={cell}>
                <Text size="xs">{topList(a.clients)}</Text>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClientTable({ clients }: { clients: ResourceAllocation['clients'] }) {
  if (clients.length === 0) {
    return <Text className="text-neutral-muted">Nenhum apontamento no período.</Text>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <Th>Cliente</Th>
            <Th>Horas efetivas</Th>
            <Th>% do total</Th>
            <Th>FTE</Th>
            <Th>Tickets</Th>
            <Th>Analistas</Th>
          </tr>
        </thead>
        <tbody>
          {clients.map((c) => (
            <tr key={c.companyId} className="hover:bg-neutral-soft-surface">
              <td className={cell}>
                <Text strong>{c.name}</Text>
              </td>
              <td className={clsx(cell, 'whitespace-nowrap')}>
                <Text>{fmtHours(c.hours)}</Text>
              </td>
              <td className={clsx(cell, 'min-w-[120px]')}>
                <Text size="xs">{fmtPercent(c.sharePercent)}</Text>
                <div className="bg-neutral-subtle mt-4xs h-1.5 overflow-hidden rounded-full">
                  <div
                    className="bg-brand-default h-full rounded-full"
                    style={{ width: `${c.sharePercent}%` }}
                  />
                </div>
              </td>
              <td className={cell}>
                <Text>{c.fte === null ? '-' : c.fte.toLocaleString('pt-BR')}</Text>
              </td>
              <td className={cell}>
                <Text>{c.ticketCount}</Text>
              </td>
              <td className={cell}>
                <Text size="xs">{topList(c.analysts)}</Text>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
